import { createRoot } from "react-dom/client";
import { useState, useEffect, useRef } from 'react';
import { motion as Motion, AnimatePresence } from 'motion/react';

const Page = ({ payload }) => {
	const { logo, landingPageGlobe, ssoPageUrl } = payload

	const [isTransitioning, setIsTransitioning] = useState(false);
	const canvasRef = useRef(null);

	// Persistent Refs for Animation State
	const dotsRef = useRef([]);
	const rotationRef = useRef(0);
	const zoomRef = useRef(1);
	const opacityRef = useRef(1);
	const speedRef = useRef(0.0003);
	const requestRef = useRef();
	const isDataLoadedRef = useRef(false);

	const onEnter = () => {
		//  go to the sso page 
		window.location.href = ssoPageUrl;
	}

	// Transition Handler
	const handleEnter = () => {
		setIsTransitioning(true);
		// Allow the "Globe Disappearing" animation to play out for 1.5s
		// Then trigger the page switch. This ensures the globe is gone before we swap.
		setTimeout(() => {
			onEnter();
		}, 200);
	};

	// --- Data Loading Effect ---
	useEffect(() => {
		if (isDataLoadedRef.current) return;

		// 1. Immediate Fallback: Fibonacci Sphere
		const generateFibonacciSphere = () => {
			const validDots = [];
			const samples = 1500;
			const phi = Math.PI * (3 - Math.sqrt(5));
			for (let i = 0; i < samples; i++) {
				const y = 1 - (i / (samples - 1)) * 2;
				const radius = Math.sqrt(1 - y * y);
				const theta = phi * i;
				const x = Math.cos(theta) * radius;
				const z = Math.sin(theta) * radius;
				validDots.push({ lat: Math.asin(y), lng: Math.atan2(z, x) });
			}
			return validDots;
		};
		dotsRef.current = generateFibonacciSphere();
		isDataLoadedRef.current = true;

		// 2. Async Image Load
		const img = new Image();
		img.src = landingPageGlobe;
		img.crossOrigin = "Anonymous";

		img.onload = () => {
			try {
				const tempCanvas = document.createElement('canvas');
				const tempCtx = tempCanvas.getContext('2d');
				if (!tempCtx) return;

				const mapWidth = 200;
				const mapHeight = 100;
				tempCanvas.width = mapWidth;
				tempCanvas.height = mapHeight;
				tempCtx.drawImage(img, 0, 0, mapWidth, mapHeight);

				const imgData = tempCtx.getImageData(0, 0, mapWidth, mapHeight).data;
				const mapDots = [];

				for (let y = 0; y < mapHeight; y++) {
					for (let x = 0; x < mapWidth; x++) {
						const i = (y * mapWidth + x) * 4;
						const brightness = (imgData[i] + imgData[i + 1] + imgData[i + 2]) / 3;
						const alpha = imgData[i + 3];

						if (alpha > 100 && brightness < 230) {
							const lng = (x / mapWidth) * Math.PI * 2 - Math.PI;
							const lat = -((y / mapHeight) * Math.PI - Math.PI / 2);
							mapDots.push({ lat, lng });
						}
					}
				}

				if (mapDots.length > 100) {
					dotsRef.current = mapDots;
				}
			} catch (e) {
				console.warn("Globe image processing failed, keeping sphere.");
			}
		};
	}, []);

	// --- Animation Loop ---
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		let width = window.innerWidth;
		let height = window.innerHeight;

		const resize = () => {
			width = window.innerWidth;
			height = window.innerHeight;
			canvas.width = width;
			canvas.height = height;
		};
		window.addEventListener('resize', resize);
		resize();

		const render = () => {
			ctx.clearRect(0, 0, width, height);
			const cx = width / 2;
			const cy = height / 2;

			const GLOBE_RADIUS_BASE = Math.min(width, height) * 0.58;
			const DOT_SIZE_BASE = 1.3;

			if (isTransitioning) {
				// "Moving far away" effect
				// Reduce scale smoothly but slower
				zoomRef.current = Math.max(0.0, zoomRef.current * 0.99);

				// Reduce opacity to ensure it disappears
				opacityRef.current = Math.max(0, opacityRef.current - 0.015);

				// Still rotate but no crazy speed
				rotationRef.current += speedRef.current;
			} else {
				rotationRef.current += speedRef.current;
			}

			const zoom = zoomRef.current;
			const rotation = rotationRef.current;
			const globalOpacity = opacityRef.current;
			const globeRadius = GLOBE_RADIUS_BASE * zoom;

			const tiltX = 0.2;
			const tiltY = 0.1;

			// Globe Color: Stronger Teal for Light Mode
			ctx.fillStyle = '#0d9488';

			const currentDots = dotsRef.current;

			for (let i = 0; i < currentDots.length; i++) {
				const dot = currentDots[i];

				let x1 = Math.cos(dot.lat) * Math.cos(dot.lng + rotation);
				let y1 = Math.sin(dot.lat);
				let z1 = Math.cos(dot.lat) * Math.sin(dot.lng + rotation);

				const y2 = y1 * Math.cos(tiltX) - z1 * Math.sin(tiltX);
				const z2 = y1 * Math.sin(tiltX) + z1 * Math.cos(tiltX);
				const x2 = x1;

				const x3 = x2 * Math.cos(tiltY) - z2 * Math.sin(tiltY);
				const z3 = x2 * Math.sin(tiltY) + z2 * Math.cos(tiltY);
				const y3 = y2;

				const perspective = 1600;
				const zDepth = globeRadius * z3;

				if (zDepth >= perspective - 50) continue;

				const scale = perspective / (perspective - zDepth);
				const x2d = cx + (globeRadius * x3) * scale;
				const y2d = cy + (globeRadius * y3) * scale;

				const isVisible = zDepth > -globeRadius * 0.8;

				if (isVisible) {
					let alpha = (z3 + 0.4);
					if (alpha < 0) alpha = 0;
					if (alpha > 1) alpha = 1;

					// Apply global transition opacity
					alpha *= globalOpacity;

					const size = DOT_SIZE_BASE * scale;

					ctx.globalAlpha = alpha * 0.5;

					ctx.beginPath();
					ctx.arc(x2d, y2d, size, 0, Math.PI * 2);
					ctx.fill();
				}
			}

			if (globalOpacity > 0.01) {
				requestRef.current = requestAnimationFrame(render);
			}
		};

		requestRef.current = requestAnimationFrame(render);

		return () => {
			if (requestRef.current) cancelAnimationFrame(requestRef.current);
			window.removeEventListener('resize', resize);
		};
	}, [isTransitioning]);

	return (
		<Motion.div
			className="absolute inset-0 w-full h-screen overflow-hidden bg-slate-50 text-slate-900 font-sans selection:bg-[#46d2c4]/20 z-50"
			// Quick fade out once the component actually unmounts (after the 1.5s delay)
			exit={{ opacity: 0 }}
			transition={{ duration: 0.5 }}>

			{/* 1. Background System - Light Mode */}
			<div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-[#f0fdfa] pointer-events-none z-0" />

			{/* Subtle Grid Pattern - Light Mode */}
			<div className="absolute inset-0 opacity-[0.03] pointer-events-none z-0"
				style={{ backgroundImage: 'radial-gradient(#0f172a 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

			{/* Globe Canvas - z-10 */}
			<canvas className="absolute inset-0 z-10" ref={canvasRef} />

			{/* Center Readability Overlay - Fade out globe behind text */}
			<div className="absolute inset-0 z-20 pointer-events-none"
				style={{ background: 'radial-gradient(circle at center, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.4) 25%, rgba(255,255,255,0) 60%)' }} />

			{/* 3. Hero Content - (No separate Header now) */}
			<div className="relative z-30 flex flex-col items-center justify-center h-full px-6 text-center mt-[-10vh]">
				<AnimatePresence>
					{!isTransitioning && (
						<Motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -100 }}
							transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
							className="flex flex-col items-center max-w-5xl">
							{/* Filerskeepers Logo - Centered above header */}
							<div className="mb-4 scale-[1.3] text-primary transform origin-center flex items-center gap-2">
								<img src={logo} />
								<span className="font-bold text-[18px] tracking-tight font-sans">filerskeepers</span>
							</div>

							<h1 className="text-6xl md:text-8xl font-bold tracking-tight mb-[12px] leading-[1.05] text-slate-900 mt-4">
								Access the <br />
								<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0d9488] to-[#46d2c4]">
									filerskeepers platform
								</span>
							</h1>
							<p className="text-xl md:text-2xl text-slate-600 font-light max-w-3xl mb-8 leading-relaxed">
								One secure gateway to global records governance, compliance automation, and legal intelligence.
							</p>

							{/* CTA Button - Solid Green & Prominent */}
							<div className="relative group mt-4">
								<div className="absolute -inset-1 bg-gradient-to-r from-[#46d2c4] to-[#2dd4bf] rounded-full blur opacity-30 group-hover:opacity-60 transition duration-200" />
								<button
									onClick={handleEnter}
									className="relative px-14 py-4 rounded-full bg-[#46d2c4] text-white text-[16px] font-bold tracking-wide hover:bg-[#3db8ac] transition-all duration-300 shadow-[0_4px_20px_-5px_rgba(70,210,196,0.5)] hover:shadow-[0_8px_30px_-5px_rgba(70,210,196,0.6)] active:scale-95 translate-y-0">
									Log in
								</button>
							</div>
						</Motion.div>
					)}
				</AnimatePresence>
			</div>
		</Motion.div>
	)
}

function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<Page payload={payload} />
	);
}

export default initPage;